/* @flow */

import { sort } from 'ramda';
import { getBraindateId } from '@e180/domain/lib/braindate/util';
import { assert, assertObject, assertArray } from '@e180/util/lib/assert';
import { isString } from '@e180/util/lib/type';

import {
  notificationErrorLevel,
  notificationSuccessLevel,
  notificationInfoLevel,
  notificationWarningLevel,
  notificationTakeOverType,
  notificationTopType,
} from 'src/shared/app/notification/constant/notificationConstants';
import VirtualBraindateEarlyReminder from 'plugin/virtual-braindate/component/notification/VirtualBraindateEarlyReminder';
import VirtualBraindateReminderNotification from 'plugin/virtual-braindate/component/notification/VirtualBraindateReminderNotification';
import VirtualBraindateReminderTakeOver from 'plugin/virtual-braindate/component/notification/VirtualBraindateReminderTakeOver';
import { braindateRoute } from 'src/shared/app/base/route/setting/routeSettings';
import { getRoutePath } from 'src/shared/app/base/route/util/routeUtils';

import type { Braindate } from '@e180/domain/lib/braindate/type';
import type { Notification } from 'src/shared/app/notification/type/notificationTypes';

// DIRTY
export function mapNotificationComponentClass(comp: any): any {
  if (isString(comp)) {
    switch (comp) {
      case 'VirtualBraindateEarlyReminder':
        return VirtualBraindateEarlyReminder;
      case 'VirtualBraindateReminderNotification':
        return VirtualBraindateReminderNotification;
      case 'VirtualBraindateReminderTakeOver':
        return VirtualBraindateReminderTakeOver;
    }
  }

  return comp;
}

// DIRTY
export function mapNotificationExcludePath(
  route: any,
  notification: Notification,
  state: any,
): ?string {
  if (isString(route)) {
    if ('braindateRoute' === route) {
      return getRoutePath(braindateRoute, state);
    }

    if ('braindateRoute:id' === route) {
      // $FlowIssue
      const braindate: Braindate = getNotificationData(notification);
      const braindateId = getBraindateId(braindate);
      const braindatePath = getRoutePath(braindateRoute, state);

      return braindatePath.replace(':id', String(braindateId));
    }
  }

  return getRoutePath(route, state);
}

/*
|------------------------------------------------------------------------------
| GETTERS
|------------------------------------------------------------------------------
*/

export function getNotificationId(notification: Notification): string {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  return notification.id;
}

export function getNotificationLevel(notification: Notification): string {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  return notification.level;
}

export function getNotificationType(notification: Notification): string {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  return notification.type;
}

export function getNotificationComponentClass(notification: Notification): any {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  return notification.componentClass;
}

export function getNotificationData(notification: Notification): ?Object {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  return notification.data;
}

// TODO: replace `any` by symbols
export function getNotificationExcludePaths(
  notification: Notification,
): Array<any> {
  assert(notification, 'notification');
  assertObject(notification, 'notification');

  const { exclude } = notification;

  return Array.isArray(exclude) ? exclude : [];
}

/*
|------------------------------------------------------------------------------
| CHECKERS
|------------------------------------------------------------------------------
*/

export function isTakeOverNotification(notification: Notification): boolean {
  return getNotificationType(notification) === notificationTakeOverType;
}

export function isTopNotification(notification: Notification): boolean {
  return getNotificationType(notification) === notificationTopType;
}

/*
|------------------------------------------------------------------------------
| FILTERS
|------------------------------------------------------------------------------
*/

export function getErrorNotifications(
  notifications: Array<Notification>,
): Array<Notification> {
  assertArray(notifications, 'notifications');

  return notifications.filter(
    (n) => getNotificationLevel(n) === notificationErrorLevel,
  );
}

export function getSuccessNotifications(
  notifications: Array<Notification>,
): Array<Notification> {
  assertArray(notifications, 'notifications');

  return notifications.filter(
    (n) => getNotificationLevel(n) === notificationSuccessLevel,
  );
}

export function getInfoNotifications(
  notifications: Array<Notification>,
): Array<Notification> {
  assertArray(notifications, 'notifications');

  return notifications.filter(
    (n) => getNotificationLevel(n) === notificationInfoLevel,
  );
}

export function getWarningNotifications(
  notifications: Array<Notification>,
): Array<Notification> {
  assertArray(notifications, 'notifications');

  return notifications.filter(
    (n) => getNotificationLevel(n) === notificationWarningLevel,
  );
}

/*
|------------------------------------------------------------------------------
| SORTING
|------------------------------------------------------------------------------
*/

export function orderNotificationsByPriority(
  notifications: Array<Notification>,
): Array<Notification> {
  assertArray(notifications, 'notifications');

  const order = [
    notificationErrorLevel,
    notificationSuccessLevel,
    notificationInfoLevel,
    notificationWarningLevel,
  ];

  return sort((a, b) => {
    const aScore = order.indexOf(getNotificationLevel(a));
    const bScore = order.indexOf(getNotificationLevel(b));

    return aScore - bScore;
  }, notifications);
}
